'use server';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';

import type { ActionResult } from '@/shared/types/actions';
import type { ExportOptions } from '@/contexts/media/types/export';
import { SessionService } from '@/contexts/authentication';
import { mediaExportJobRepository } from '@/contexts/media/repositories/MediaExportJobRepository';

export interface StoryExport {
  id: string;
  exportType: string;
  status: string;
  progress?: number;
  resultUrl?: string;
  bookId: string;
  bookTitle?: string;
  audioStoryId?: string;
  audioStoryName?: string;
  /** What was asked for — paper, upscale, edge fill, archive. */
  options?: ExportOptions;
  createdAt: string;
}

export async function getStoryExports(
  storyId: string,
): Promise<ActionResult<StoryExport[]>> {
  try {
    const familyId = await SessionService.getFamilyId();
    if (!familyId) return { success: false, error: 'Not authenticated' };

    const jobs = await mediaExportJobRepository.listByStoryId(storyId, familyId);

    // Include both completed and in-progress jobs so UI can restore progress state
    const inProgressStatuses = ['pending', 'downloading', 'processing', 'uploading'];
    const relevant = jobs.filter(j =>
      (j.status === 'completed' && (j.resultUrl || j.videoUrl)) ||
      inProgressStatuses.includes(j.status)
    );
    if (relevant.length === 0) return { success: true, data: [] };
    const completed = relevant.filter(j => j.status === 'completed');

    // Resolve book titles
    const bookIds = [...new Set(relevant.map(j => j.bookId))];
    const { getIllustrationRepository } = await import('@/contexts/illustrations/repositories');
    const bookRepo = getIllustrationRepository();
    const bookTitles = new Map<string, string>();
    for (const id of bookIds) {
      const book = await bookRepo.getById(id, familyId);
      if (book) bookTitles.set(id, book.title);
    }

    // Resolve audio story names (prefer voice name for display)
    const audioStoryIds = [...new Set(relevant.map(j => j.audioStoryId).filter(Boolean))] as string[];
    const audioStoryNames = new Map<string, string>();
    if (audioStoryIds.length > 0) {
      const { getAudioStoryRepository } = await import('@/contexts/audio-stories/repositories');
      const audioRepo = getAudioStoryRepository();
      const voiceIdsToResolve = new Set<string>();

      // First pass: collect audio stories and note which voice IDs need resolving
      const audioStories = new Map<string, Awaited<ReturnType<typeof audioRepo.getById>>>();
      for (const id of audioStoryIds) {
        const as = await audioRepo.getById(id);
        if (as) {
          audioStories.set(id, as);
          if (!as.name && !as.defaultVoiceName && as.defaultVoiceId) {
            voiceIdsToResolve.add(as.defaultVoiceId);
          }
        }
      }

      // Resolve voice names from catalog if needed
      const voiceNames = new Map<string, string>();
      if (voiceIdsToResolve.size > 0) {
        try {
          const { getVoiceById } = await import('@/contexts/ai/services/generation/VoiceCatalogService');
          for (const voiceId of voiceIdsToResolve) {
            const voice = await getVoiceById(voiceId);
            if (voice?.name) voiceNames.set(voiceId, voice.name);
          }
        } catch { /* voice catalog unavailable — fall back to ID */ }
      }

      // Build display names
      for (const [id, as] of audioStories) {
        if (!as) continue;
        // Resolve display: name > voiceName > catalog name > gender label > voiceId
        let displayName = as.name
          || as.defaultVoiceName
          || (as.defaultVoiceId && voiceNames.get(as.defaultVoiceId))
          || as.defaultVoiceId
          || id.slice(0, 8);
        // Pass through raw gender identifiers — UI will translate via i18n
        audioStoryNames.set(id, displayName);
      }
    }

    // Refresh SAS URLs for exports (the stored URL may have stale query params)
    const { BookImageStorage } = await import('@/contexts/media/storage/BookImageStorage');

    const exports: StoryExport[] = await Promise.all(
      relevant.map(async (j) => {
        const rawUrl = j.resultUrl || j.videoUrl;
        // Generate fresh SAS URL for streaming (no Content-Disposition: attachment)
        let freshUrl = rawUrl;
        if (rawUrl) {
          try {
            freshUrl = await BookImageStorage.refreshSasUrl(rawUrl);
          } catch {
            freshUrl = rawUrl;
          }
        }

        return {
          id: j.id,
          exportType: j.exportType ?? 'video',
          status: j.status,
          progress: (j as any).progress ?? undefined,
          resultUrl: freshUrl,
          bookId: j.bookId,
          bookTitle: bookTitles.get(j.bookId),
          audioStoryId: j.audioStoryId,
          audioStoryName: j.audioStoryId ? audioStoryNames.get(j.audioStoryId) : undefined,
          options: j.options,
          createdAt: j.createdAt,
        };
      })
    );

    return { success: true, data: exports };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('getStoryExports', e) };
  }
}
